import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactElement, TouchEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ManuscriptChapter } from '@xdocs/schemas';
import { Button, EmptyState, Skeleton, cn } from '@xdocs/ui';
import { ManuscriptBodyHtml, READER_THEME_PALETTE, useReaderPosition, useReaderPrefs } from '../../features/reader/index.js';
import { useReaderT } from '../../features/reader/useReaderT.js';
import { ReaderChapterDrawer } from './ReaderChapterDrawer.js';
import { ReaderToolbar } from './ReaderToolbar.js';
import { useReadDocument } from './useReadDocument.js';
import styles from './ReadPage.module.css';

function sortedChapters(chapters: readonly ManuscriptChapter[] | undefined): ManuscriptChapter[] {
  if (!chapters) return [];
  return [...chapters].sort((a, b) => a.order - b.order);
}

/**
 * `/read/:docId` (F66) — distraction-free e-reader. Read-only over the
 * reader-tier ACL set (owner/staffOverride/user-grant/public/link, all
 * resolved server-side by `document.get`/`version.get`); this page never
 * decides access itself, it only renders whatever those ops return or
 * surfaces their `FORBIDDEN`/`NOT_FOUND` as a friendly gate (F66 AC).
 */
export function ReadPage(): ReactElement {
  const { docId } = useParams<{ docId: string }>();
  const documentId = docId ?? '';
  const navigate = useNavigate();
  const { t } = useReaderT();
  const prefs = useReaderPrefs();

  const {
    document,
    isLoading,
    isError,
    error,
    version,
    isVersionLoading,
    isVersionError,
    hasNewerVersion,
    switchToLatestVersion,
  } = useReadDocument(documentId);

  const { position, savePosition } = useReaderPosition(documentId, version?.id ?? null);

  const chapters = useMemo(() => sortedChapters(version?.contentJsonb.chapters), [version]);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const [newVersionDismissed, setNewVersionDismissed] = useState(false);
  const restoredForVersionRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Resume position: once per newly-loaded version, jump to the recorded
  // chapter (falls back to the first chapter — E20 §5.10-3 offline mirror
  // included, since `useReaderPosition` already reconciles the
  // WS/IndexedDB sources before returning `position`).
  useEffect(() => {
    if (!version || chapters.length === 0) return;
    if (restoredForVersionRef.current === version.id) return;
    restoredForVersionRef.current = version.id;
    const resumeChapter = position && chapters.some((c) => c.id === position.chapterId) ? position.chapterId : (chapters[0]?.id ?? null);
    setActiveChapterId(resumeChapter);
  }, [version, chapters, position]);

  const activeIndex = chapters.findIndex((c) => c.id === activeChapterId);
  const activeChapter = activeIndex >= 0 ? chapters[activeIndex] : undefined;

  const goToChapter = useCallback(
    (chapterId: string) => {
      setActiveChapterId(chapterId);
      savePosition({ chapterId, offsetPercent: 0 });
      // `Element.scrollTo` isn't implemented in jsdom (this project's test
      // environment) — guarded rather than assumed, so tests never trip an
      // unhandled error over what's purely a real-browser affordance.
      containerRef.current?.scrollTo?.({ top: 0 });
    },
    [savePosition],
  );

  const goPrevious = useCallback(() => {
    if (activeIndex > 0) {
      const prev = chapters[activeIndex - 1];
      if (prev) goToChapter(prev.id);
    }
  }, [activeIndex, chapters, goToChapter]);

  const goNext = useCallback(() => {
    if (activeIndex >= 0 && activeIndex < chapters.length - 1) {
      const next = chapters[activeIndex + 1];
      if (next) goToChapter(next.id);
    }
  }, [activeIndex, chapters, goToChapter]);

  // Keyboard nav (F66 summary: "Pagination/scroll toggle ... keyboard nav").
  useEffect(() => {
    if (prefs.layoutMode !== 'paginated') return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'ArrowLeft') goPrevious();
      else if (event.key === 'ArrowRight') goNext();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [prefs.layoutMode, goPrevious, goNext]);

  // Mobile swipe nav (F66 summary: "mobile-first (tap zones, swipe)").
  const touchStartXRef = useRef<number | null>(null);
  const onTouchStart = useCallback((event: TouchEvent) => {
    touchStartXRef.current = event.touches[0]?.clientX ?? null;
  }, []);
  const onTouchEnd = useCallback(
    (event: TouchEvent) => {
      const startX = touchStartXRef.current;
      touchStartXRef.current = null;
      if (startX === null || prefs.layoutMode !== 'paginated') return;
      const endX = event.changedTouches[0]?.clientX ?? startX;
      const delta = endX - startX;
      const SWIPE_THRESHOLD_PX = 60;
      if (delta > SWIPE_THRESHOLD_PX) goPrevious();
      else if (delta < -SWIPE_THRESHOLD_PX) goNext();
    },
    [prefs.layoutMode, goPrevious, goNext],
  );

  // Scroll-mode position tracking: nearest chapter + coarse scroll percent,
  // debounced via rAF so it never fires more than once per frame.
  const rafRef = useRef<number | null>(null);
  const onScroll = useCallback(() => {
    if (prefs.layoutMode !== 'scroll') return;
    const el = containerRef.current;
    if (!el) return;
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      const headings = Array.from(el.querySelectorAll<HTMLElement>('[data-chapter-id]'));
      let nearest: HTMLElement | undefined;
      for (const heading of headings) {
        if (heading.offsetTop - el.scrollTop <= 80) nearest = heading;
        else break;
      }
      const chapterId = nearest?.dataset['chapterId'];
      const maxScroll = el.scrollHeight - el.clientHeight;
      const offsetPercent = maxScroll > 0 ? Math.min(100, Math.max(0, (el.scrollTop / maxScroll) * 100)) : 0;
      if (chapterId) {
        setActiveChapterId(chapterId);
        savePosition({ chapterId, offsetPercent });
      }
    });
  }, [prefs.layoutMode, savePosition]);

  useEffect(() => () => {
    if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
  }, []);

  if (!docId) {
    return <EmptyState kind="error" title={t('reader:notFound.title')} />;
  }

  if (isLoading) {
    return (
      <div className={cn(styles['page'])} aria-busy="true">
        <Skeleton shape="text-line" width="30%" />
        <Skeleton shape="card" count={3} />
      </div>
    );
  }

  if (isError) {
    if (error?.code === 'NOT_FOUND') {
      return (
        <EmptyState
          kind="error"
          title={t('reader:notFound.title')}
          action={{ label: t('reader:notFound.cta'), onClick: () => void navigate('/documents') }}
        />
      );
    }
    if (error?.code === 'FORBIDDEN') {
      return (
        <EmptyState
          kind="error"
          title={t('reader:forbidden.title')}
          description={t('reader:forbidden.description')}
          action={{ label: t('reader:forbidden.cta'), onClick: () => void navigate('/documents') }}
        />
      );
    }
    return <EmptyState kind="error" title={t('reader:error.title')} action={{ label: t('reader:error.retry'), onClick: () => window.location.reload() }} />;
  }

  if (!document) {
    return <EmptyState kind="error" title={t('reader:notFound.title')} />;
  }

  const palette = READER_THEME_PALETTE[prefs.themeId];
  const readerVars: CSSProperties = {
    '--reader-bg': palette.background,
    '--reader-text': palette.text,
    '--reader-font-scale': prefs.fontScale,
  } as CSSProperties;

  const progressLabel = activeIndex >= 0 && chapters.length > 0 ? t('reader:progress', { current: activeIndex + 1, total: chapters.length }) : '';

  return (
    <div className={cn(styles['page'])} style={readerVars} data-testid="read-page">
      <ReaderToolbar
        progressLabel={progressLabel}
        prefs={prefs}
        onClose={() => void navigate(`/documents/${documentId}`)}
        onOpenChapters={() => setChaptersOpen(true)}
      />

      {hasNewerVersion && !newVersionDismissed && (
        <div role="status" className={cn(styles['newVersionBanner'])}>
          <div>
            <p className={cn(styles['newVersionTitle'])}>{t('reader:newVersion.title')}</p>
            <p className={cn(styles['newVersionDescription'])}>{t('reader:newVersion.description')}</p>
          </div>
          <div className={cn(styles['newVersionActions'])}>
            <Button variant="ghost" size="sm" onClick={() => setNewVersionDismissed(true)}>
              {t('reader:newVersion.dismiss')}
            </Button>
            <Button variant="primary" size="sm" onClick={switchToLatestVersion}>
              {t('reader:newVersion.cta')}
            </Button>
          </div>
        </div>
      )}

      {isVersionLoading && (
        <div className={cn(styles['body'])} aria-busy="true">
          <Skeleton shape="text-line" width="40%" />
          <Skeleton shape="text-line" count={6} />
        </div>
      )}

      {!isVersionLoading && isVersionError && (
        <EmptyState kind="error" title={t('reader:error.title')} action={{ label: t('reader:error.retry'), onClick: () => window.location.reload() }} />
      )}

      {!isVersionLoading && !isVersionError && chapters.length === 0 && (
        <EmptyState kind="first-use" title={t('reader:empty.title')} description={t('reader:empty.description')} mascotAlt="" />
      )}

      {!isVersionLoading && !isVersionError && chapters.length > 0 && (
        <div
          ref={containerRef}
          className={cn(styles['body'])}
          onScroll={onScroll}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <article className={cn(styles['article'])}>
            {prefs.layoutMode === 'paginated' && activeChapter && (
              <ReaderChapter chapter={activeChapter} />
            )}
            {prefs.layoutMode === 'scroll' &&
              chapters.map((chapter) => <ReaderChapter key={chapter.id} chapter={chapter} anchor />)}

            {prefs.layoutMode === 'paginated' && (
              <nav className={cn(styles['chapterNav'])} aria-label={t('reader:chapters.title')}>
                <Button
                  variant="ghost"
                  disabled={activeIndex <= 0}
                  leadingIcon={<ChevronLeft size={16} aria-hidden="true" />}
                  onClick={goPrevious}
                >
                  {t('reader:nav.previous')}
                </Button>
                <Button variant="primary" disabled={activeIndex >= chapters.length - 1} onClick={goNext}>
                  <span className={cn(styles['navNext'])}>
                    {t('reader:nav.next')}
                    <ChevronRight size={16} aria-hidden="true" />
                  </span>
                </Button>
              </nav>
            )}
          </article>
        </div>
      )}

      <ReaderChapterDrawer
        open={chaptersOpen}
        onClose={() => setChaptersOpen(false)}
        chapters={chapters}
        activeChapterId={activeChapterId}
        onSelectChapter={goToChapter}
      />
    </div>
  );
}

function ReaderChapter({ chapter, anchor = false }: { chapter: ManuscriptChapter; anchor?: boolean }): ReactElement {
  return (
    <section data-chapter-id={anchor ? chapter.id : undefined} className={cn(styles['chapter'])}>
      <div className={cn(styles['chapterEyebrow'])}>{chapter.order + 1}</div>
      <h1 className={cn(styles['chapterTitle'])}>{chapter.title}</h1>
      <ManuscriptBodyHtml html={chapter.body} className={cn(styles['bodyContent'])} />
    </section>
  );
}
